/*
 * Copyright (c) 2024 SARL SIGMA POLYNESIA
 */

import { SetStateAction, useCallback, useMemo, useState } from 'react'
import usePaginationQuery from '../../../../client/js/components/pagination/usePaginationQuery'
import { useQuery } from '@tanstack/react-query'
import { deleteProspecting, findManyProspecting } from '../../lib/rest/prospectingAPIClient'
import { getHalEmbedded, getHalPageTotalElements, HalDocument } from '@jalik/hal'
import { QueryParams } from '../../../../client/js/hooks/useQueryParams'
import useSelection from '../../../../client/js/components/selection/useSelection'
import SelectionAlert from '../../../../client/js/components/selection/SelectionAlert'
import LoadingHelper from '../../../../client/js/components/LoadingHelper'
import ProspectingList from './ProspectingList'
import DeleteSelectionModal from '../../../../client/js/components/modals/DeleteSelectionModal'
import ListCard from '../../../../client/js/components/ListCard'
import RequirePerm from '../../../auth/components/RequirePerm'
import { FormattedMessage } from 'react-intl'
import SearchForm from '../../../../client/js/components/search/SearchForm'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSyncAlt } from '@fortawesome/free-solid-svg-icons/faSyncAlt'
import { faFilter } from '@fortawesome/free-solid-svg-icons/faFilter'
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash'
import Filters from '../../../../client/js/components/Filters'
import ProspectingStatusFilter from '../filters/ProspectingStatusFilter'
import EstateTransactionFilter from '../filters/EstateTransactionFilter'
import EstateTypeFilter from '../filters/EstateTypeFilter'
import FurnishedFilter from '../filters/FurnishedFilter'
import EquippedFilter from '../filters/EquippedFilter'
import { getProspectingCreateLink } from '../../lib/links/prospectingLinks'
import Filter from '../Filter'
import CustomerInput from '../inputs/CustomerInput'
import { handleValueChange } from '../../../../client/js/lib/form'
import { Button } from '@mantine/core'
import AnchorLink from '../../../../client/js/components/AnchorLink'
import Toolbar from '../../../../client/js/components/Toolbar'

export type ProspectingListQuery = {
  limit?: string | number,
  page?: string | number,
  search?: string,
  transaction?: string[],
  type?: string[],
  available?: string,
  exclusivity?: string,
  published?: string,
  furnished?: string,
  equipped?: string,
  succeeded?: string,
  withCharges?: string,
  customerId?: string,
  estateId?: string,
}

export type ProspectingListCardProps = {
  createLink?: string;
  fetcher? (params?: ProspectingListQuery): Promise<HalDocument<{ prospecting: Prospecting[] }>>;
  onQueryChange (query: SetStateAction<ProspectingListQuery>): void;
  query: ProspectingListQuery;
}

const initialQuery: ProspectingListQuery = {
  limit: '10',
  page: '1'
}

function ProspectingListCard (props: ProspectingListCardProps) {
  const {
    createLink = getProspectingCreateLink(),
    fetcher = findManyProspecting,
    onQueryChange, query
  } = props

  const [totalElements, setTotalElements] = useState<number>(0)
  const pagination = usePaginationQuery(totalElements)
  const { page, limit } = pagination

  const queryParams = useMemo(() => {
    return {
      ...initialQuery,
      ...query,
      page,
      limit
    }
  }, [limit, page, query])

  // Items loading.
  const listQuery = useQuery({
    enabled: true,
    queryKey: ['prospectingList', queryParams],
    queryFn: () => fetcher(queryParams).then((r) => {
      setTotalElements(getHalPageTotalElements(r))
      return r
    })
  })
  const items = useMemo(() => (
    (listQuery.data != null
      ? getHalEmbedded(listQuery.data)?.prospecting
      : null) || []
  ), [listQuery.data])

  const setFilter = useCallback((name: string, value: unknown) => {
    onQueryChange((s: QueryParams) => ({ ...s, page: '1', [name]: value }))
  }, [onQueryChange])

  const setSearch = useCallback((search: string) => {
    setFilter('search', search)
  }, [setFilter])

  // Items selection.
  const [selectedItems, setSelectedItems] = useState<number[]>([])
  const selection = useSelection<number>({
    multiple: true,
    items: selectedItems,
    onChange: setSelectedItems,
    source: items.map((el) => el.id)
  })

  // Items deletion.
  const handleDelete = useCallback((id: number) => deleteProspecting(id), [])
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const toggleDeleteModal = useCallback(() => {
    setIsDeleteModalOpen((s) => !s)
  }, [])

  // Items filtering.
  const [isFiltersVisible, setIsFiltersVisible] = useState(true)
  const toggleFilters = useCallback(() => {
    setIsFiltersVisible((s) => !s)
  }, [])

  return (
    <ListCard
      filters={<Filters in={isFiltersVisible}>
        <ProspectingStatusFilter
          onChange={setFilter}
          value={queryParams.succeeded}
        />
        <EstateTransactionFilter
          onChange={setFilter}
          value={queryParams.transaction}
        />
        <EstateTypeFilter
          onChange={setFilter}
          value={queryParams.type}
        />
        <FurnishedFilter
          onChange={setFilter}
          value={queryParams.furnished}
        />
        <EquippedFilter
          onChange={setFilter}
          value={queryParams.equipped}
        />
        <Filter>
          <CustomerInput
            onChange={handleValueChange('customerId', setFilter)}
            value={queryParams.customerId}
          />
        </Filter>
      </Filters>}
      pagination={pagination}
      selection={selection}
      selectionActions={<SelectionAlert>
        <RequirePerm
          permissions={['write:prospecting']}
          render={() => (
            <Button onClick={toggleDeleteModal}>
              <FontAwesomeIcon
                fixedWidth
                icon={faTrash}
              />
            </Button>
          )}
        />
      </SelectionAlert>}
      toolbar={<Toolbar>
        <RequirePerm
          permissions={['write:prospecting']}
          render={() => (
            <Button
              variant="filled"
              component={AnchorLink}
              to={createLink}
            >
              <FormattedMessage
                id="create_prospecting"
                defaultMessage="Créer une prospection"
              />
            </Button>
          )}
        />
        <SearchForm
          initialValue={queryParams.search}
          onSubmit={setSearch}
          submitting={listQuery.isFetching}
        />
        <Button
          loading={listQuery.isFetching}
          onClick={() => {
            listQuery.refetch()
          }}
        >
          <FontAwesomeIcon
            fixedWidth
            icon={faSyncAlt}
          />
        </Button>
        <Button
          // todo active={isFiltersVisible}
          onClick={toggleFilters}
        >
          <FontAwesomeIcon
            fixedWidth
            icon={faFilter}
          />
        </Button>
      </Toolbar>}
    >
      <LoadingHelper
        query={listQuery}
        render={() => (
          <ProspectingList items={items} />
        )}
      />

      {isDeleteModalOpen
        ? <DeleteSelectionModal
          opened={isDeleteModalOpen}
          items={selection.items}
          onConfirm={handleDelete}
          onSuccess={() => {
            selection.clear()
            listQuery.refetch()
          }}
          onClose={toggleDeleteModal}
        />
        : null}
    </ListCard>
  )
}

export default ProspectingListCard
